import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/compat/router';
import dynamic from 'next/dynamic';
import _ from 'lodash';

import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import { useDispensaryCategory } from 'src/hooks/use-dispensary-category';
import { useCollectionFallbackForProductsRoute } from 'src/dispensary/hooks/use-collection-fallback-for-products-route';
import { tracker } from 'utils/analytics';
import { searchResultsViewed } from 'utils/ecommerce-analytics';

import { MobileOnly } from 'shared/components';
import SubcategoriesPills from 'components/subcategories-pills';
import { CategorySubcategoryPills } from 'components/category-subcategory-pills';
import { useMenuHeaderHeading } from 'components/menu-header/use-menu-header-heading';
import { Typography } from 'src/typography';

import Menu from 'components/menu';
import { MainDispensaryLayout } from 'components/layouts/main-dispensary-layout';
import useStores from 'shared/hooks/use-stores';
import { AmplitudeEvents, useAmplitude } from 'shared/hooks/use-amplitude';
import { BreadCrumbs } from 'src/components/bread-crumbs';
import { InlineCategoriesList } from 'src/components/inline-categories-list';
import { useProductsForCategoryPage } from '../hooks/use-products-for-category-page';

// Loaded on demand, only when the :category param resolves to a custom category
// collection, to avoid pulling the (heavier) collections page code into the
// standard products/category bundle.
const DispensaryCollectionsPage = dynamic(() =>
  import('src/dispensary/collections').then((mod) => mod.DispensaryCollectionsPage)
);

export default function DispensaryCategoryPage() {
  const { dispensary } = useDispensary();
  const category = useDispensaryCategory();
  const collectionFallback = useCollectionFallbackForProductsRoute(category);
  const heading = useMenuHeaderHeading({ category, page: 'category' });

  const { products, loading, totalCount, totalPages, productsCarouselQueries } = useProductsForCategoryPage({
    perPage: 100,
  });

  const { UI } = useStores();
  const amplitude = useAmplitude();
  const router = useRouter();
  const inAllProductsView = UI.onSearchPage;
  const { categoriesSubcategories, subcategories } = useMenuFilterOptions();
  const hasSubcategories = subcategories?.length > 0;
  const categoryName = router?.query?.category;
  const searchQuery = router?.query?.search;

  useEffect(() => {
    if (!_.isEmpty(categoryName) && !_.isEmpty(dispensary?.id)) {
      amplitude.log(AmplitudeEvents.menu.viewCategory, {
        categoryName,
        dispensaryId: dispensary.id,
        dispensaryName: dispensary?.name,
        menuVariant: UI.analyticsMenuVariant,
      });
    }
    // rationale: exhaustive deps cause these events to be fired more than we want
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryName]);

  useEffect(() => {
    if (!loading && searchQuery && dispensary && products) {
      tracker.setContext({
        activeDispensary: dispensary,
      });

      tracker.searchResults({ query: searchQuery, hasResults: products.length > 0, products });
      searchResultsViewed(searchQuery, products);
    }
    // rationale: exhaustive deps cause these events to be fired more than we want
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, searchQuery]);

  if (!dispensary) {
    return null;
  }

  // The :category param didn't match a real category, but it does match a collection.
  // Render the collection so /products/:slug works the same as /collections/:slug,
  // whether or not the collection is configured to appear as a custom category.
  if (collectionFallback) {
    return <DispensaryCollectionsPage slugOverride={collectionFallback.slug} />;
  }

  return (
    <>
      <MobileOnly>
        <BreadCrumbsContainer adjustForFilterPills={hasSubcategories}>
          <BreadCrumbs />
        </BreadCrumbsContainer>
        <CategoryHeadingContainer adjustForFilterPills={hasSubcategories}>
          <Typography.Heading size='medium' tag='h1' casing='capitalize' data-testid='menu-header-title'>
            {heading}
          </Typography.Heading>
        </CategoryHeadingContainer>

        <FilterPillsContainer>
          {inAllProductsView ? (
            <CategorySubcategoryPills categoriesSubcategories={categoriesSubcategories} />
          ) : (
            <SubcategoryPillsContainer marginTop='20px' />
          )}
        </FilterPillsContainer>
      </MobileOnly>
      <Menu
        totalCount={totalCount}
        totalPages={totalPages}
        products={products}
        loading={loading}
        dispensary={dispensary}
        page='category'
        productsCarouselQueries={productsCarouselQueries}
      />

      <InlineCategoriesList />
    </>
  );
}

DispensaryCategoryPage.layout = MainDispensaryLayout;

const BreadCrumbsContainer = styled.div`
  padding-top: 25px;
  margin-bottom: ${({ adjustForFilterPills }) => (adjustForFilterPills ? `-16px` : '0')};
  padding-left: ${({ adjustForFilterPills }) => (adjustForFilterPills ? `3px` : '0')};
`;

const SubcategoryPillsContainer = styled(SubcategoriesPills)`
  margin-top: ${({ marginTop }) => marginTop};
`;

const FilterPillsContainer = styled.div`
  margin-bottom: 16px;
`;

const CategoryHeadingContainer = styled.div`
  padding: ${({ adjustForFilterPills }) => (adjustForFilterPills ? '32px' : '16px')} 0 0 25px;
`;
